// マスタ閲覧ページ (読み取り専用)。
//
// ルート: /console/master-view/:masterKey/:id/
//
// 機能:
//   - 全マスタに対応した汎用ビューワ (MASTER_DEFS による定義ベース)
//   - 指定レコードを InfoList で情報テーブル表示
//   - 子リストが定義されているマスタは「タブ + 一覧表」で配下を表示
//
// マスタ編集ページからリンクは貼らず、編集と閲覧を分離している。

import { useState, useEffect } from "react";
import {
  Box, Stack, Typography, Chip, Paper, Tabs, Tab, Alert,
  Table, TableHead, TableBody, TableRow, TableCell,
} from "@mui/material";
import StorageIcon from "@mui/icons-material/Storage";
import { Navigate, Route, Routes, useNavigate, useParams } from "react-router-dom";
import { useFullDb } from "../../../api/dbSlice";
import { InfoList, InfoListColDef } from "../../../components/InfoList";
import { useAutoHeaderTitle } from "../../../components/Header";
import { PageContext } from "../PageContext";
import StyledButton from "../../../components/StyledButton";
import { DataLink, MasterLink } from "../../../components/links/EntityLinks";
import { MASTER_DEFS, collectionForMasterKey } from "./masterViewDefs";
import { ContentLoading } from "../../../components/Loading";

// =====================================================================
// ルートラッパ
// =====================================================================

// Pages.tsx で `master-view/*` として登録されるため、内部で :masterKey/:id を切る Routes を持つ。
export default function MasterViewRoute({ pageContext }: { pageContext: PageContext }) {
  return (
    <Routes>
      <Route path=":masterKey/:id/" element={<MasterViewPage pageContext={pageContext} />} />
      <Route path="*" element={<Navigate to="/console/" replace />} />
    </Routes>
  );
}

// =====================================================================
// ページ本体
// =====================================================================

function MasterViewPage({ pageContext }: { pageContext: PageContext }) {
  const { masterKey, id } = useParams<{ masterKey: string; id: string }>();
  const { db, loading } = useFullDb(pageContext.api.indexedDB, pageContext.api.currentUser.id);
  const navigate = useNavigate();
  const def = masterKey ? MASTER_DEFS[masterKey] : undefined;

  useAutoHeaderTitle(
    () => ({ title: def?.title ?? "マスタ", prefix: "", suffix: id ? ` #${id}` : "" }),
    [def?.title, id],
  );

  // 別マスタ間遷移時の初期タブをリセット
  const [tab, setTab] = useState<string>(() => def?.childLists?.[0]?.key ?? "");
  useEffect(() => {
    setTab(def?.childLists?.[0]?.key ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [masterKey, id]);

  if (!masterKey || !def) {
    return (
      <Stack p={3} maxWidth={1100} mx="auto" gap={2}>
        <Alert severity="error">マスタ「{masterKey}」は閲覧未対応です。</Alert>
        <Stack direction="row" justifyContent="flex-start">
          <StyledButton buttonStyle="text" onClick={() => navigate("/console/")}>← トップへ戻る</StyledButton>
        </Stack>
      </Stack>
    );
  }

  if (loading) {
    return <ContentLoading />;
  }

  const all = collectionForMasterKey(masterKey, db);
  const record = all.find((r) => String(r.id) === String(id));

  if (!record) {
    return (
      <Stack p={3} maxWidth={1100} mx="auto" gap={2}>
        <Alert severity="error">{def.title} #{id} は存在しません。</Alert>
        <Stack direction="row" justifyContent="flex-start">
          <StyledButton buttonStyle="text" onClick={() => navigate(-1)}>← 戻る</StyledButton>
        </Stack>
      </Stack>
    );
  }

  const cols: InfoListColDef[] = def.infoFields.map((f) => ({
    label: f.label,
    context: f.render(record, db),
  }));

  const childLists = def.childLists ?? [];
  const currentTab = tab || childLists[0]?.key || "";

  return (
    <Stack p={3} gap={3} maxWidth={1200} mx="auto">
      <Stack direction="row" alignItems="center" gap={1}>
        <StorageIcon color="action" />
        <Typography variant="h4" fontWeight="bold">{def.title}</Typography>
        <Chip size="small" color="info" label="マスタ閲覧" />
      </Stack>
      <Paper variant="outlined" sx={{ p: 2, backgroundColor: "grey.50" }}>
        <Typography variant="h5" fontWeight={600}>{def.recordTitle(record, db)}</Typography>
      </Paper>

      <InfoList cols={cols} columns={3} />

      {childLists.length > 0 && (
        <Paper variant="outlined">
          <Tabs value={currentTab} onChange={(_, v) => setTab(v)}>
            {childLists.map((cl) => {
              const children = cl.rows(record, db);
              return <Tab key={cl.key} value={cl.key} label={`${cl.label} (${children.length})`} />;
            })}
          </Tabs>
          <Box p={2}>
            {childLists.map((cl) => {
              if (cl.key !== currentTab) {
                return null;
              }
              const children = cl.rows(record, db);
              if (children.length === 0) {
                return (
                  <Alert key={cl.key} severity="info">
                    {cl.empty ?? `${cl.label}はまだ登録されていません`}
                  </Alert>
                );
              }
              const kind: "master" | "data" = cl.kind ?? "master";
              return (
                <Table key={cl.key} size="small">
                  <TableHead>
                    <TableRow>
                      {kind === "data" && <TableCell sx={{ fontWeight: "bold" }}>{cl.dataEntityType ? "" : "ID"}</TableCell>}
                      {cl.columns.map((c, i) => (
                        <TableCell key={i} sx={{ fontWeight: "bold", width: c.width }}>{c.label}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {children.map((row) => (
                      <TableRow key={row.id} hover>
                        {kind === "data" && (
                          <TableCell>
                            {cl.dataEntityType
                              ? <DataLink type={cl.dataEntityType} id={row.id} parentId={cl.dataParentId?.(row, record)} />
                              : `#${row.id}`}
                          </TableCell>
                        )}
                        {cl.columns.map((c, i) => {
                          const content = c.render(row, db);
                          if (kind === "master" && i === 0 && cl.detailMasterKey) {
                            return (
                              <TableCell key={i}>
                                <MasterLink type={cl.detailMasterKey} id={row.id} name={content} />
                              </TableCell>
                            );
                          }
                          return <TableCell key={i}>{content}</TableCell>;
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              );
            })}
          </Box>
        </Paper>
      )}
    </Stack>
  );
}
